/**
 * SYSTEM SETTINGS MODULE — County distribution editor (per state).
 *
 * Opt-in per state: turn "Distribute by county" on, then assign a distributor to
 * each county — by painting on the state map (arm a distributor, click counties)
 * or via the searchable list. Data is account-scoped reference data written
 * through the shared referenceDataService (sales_state_modes + sales_counties,
 * migration 0037); the distribution map reads it live.
 *
 * Uses the top-level map-core renderer for the county map (same shared package
 * the distribution-map module renders with) via the shared countyGeo accessor.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  buildMapSvg,
  countyGeometry,
  zoomViewBox,
  resolveFills,
} from "../../../../../map-core/render.js";
import {
  resolveStateToFips,
  stateMeta,
  loadCountyBundle,
} from "../../../shared/utils/countyGeo.js";
import {
  listSalesCountiesForState,
  setCountyDistributor,
  setCountyDistributorBulk,
  setSalesStateMode,
} from "../../../shared/utils/referenceDataService.js";

// The map's DEFAULT_PALETTE (Okabe–Ito, colorblind-safe), reused so the two
// screens at least draw from the same set of colors.
//
// ⚠️ These are NOT the colors the published map uses, and they are not meant to
// be. Real map colors live per-map in `map_distributors` (the distribution-map
// module owns that table; System Settings must not read it — MODULES.md rule 6),
// and each map's owner customizes them. Here color is only a way to tell
// distributors apart while painting: it is assigned by index over the sorted
// distributor names, so the same distributor can appear grey here and blue on
// the published map. The legend below says so.
const PALETTE = [
  "#0072b2", "#e69f00", "#009e73", "#cc79a7",
  "#56b4e9", "#d55e00", "#f0e442", "#999999",
];

export default function CountyModal({
  stateName,
  initialMode = "state",
  distributorOptions = [],
  onClose,
  onChanged,
}) {
  const [fips, setFips] = useState(null);
  const [iso, setIso] = useState(null);
  const [bbox, setBbox] = useState(null);
  const [geo, setGeo] = useState(null);              // countyGeometry(bundle)
  const [assignments, setAssignments] = useState({}); // { countyFips: distributorName }
  const [extraNames, setExtraNames] = useState([]);   // distributors added in-modal
  const [mode, setMode] = useState(initialMode);
  const [armed, setArmed] = useState(null);           // distributor armed for painting
  const [newDist, setNewDist] = useState("");
  const [search, setSearch] = useState("");
  const [bulkName, setBulkName] = useState("");
  const [hoverFips, setHoverFips] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const dirty = useRef(false); // any write happened -> tell parent on close

  // Resolve state -> FIPS, load geometry + existing assignments.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const f = await resolveStateToFips(stateName);
        if (!f) throw new Error(`We don't have county data for "${stateName}". Check the state name spelling.`);
        const meta = await stateMeta(f);
        const bundle = await loadCountyBundle(f);
        const existing = await listSalesCountiesForState(f);
        if (!alive) return;
        setFips(f);
        setIso(meta?.iso || null);
        setBbox(meta?.bbox || null);
        setGeo(countyGeometry(bundle));
        const map = {};
        for (const r of existing) if (r.distributor) map[r.county_fips] = r.distributor;
        setAssignments(map);
      } catch (e) {
        if (alive) setErr(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [stateName]);

  // All distributor names available to assign (sorted, stable) + a palette color.
  const names = useMemo(() => {
    const set = new Set();
    for (const n of distributorOptions) if (n && n.trim()) set.add(n.trim());
    for (const n of Object.values(assignments)) if (n) set.add(n);
    for (const n of extraNames) if (n && n.trim()) set.add(n.trim());
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [distributorOptions, assignments, extraNames]);

  const colorOf = useMemo(() => {
    const m = new Map();
    names.forEach((n, i) => m.set(n, PALETTE[i % PALETTE.length]));
    return m;
  }, [names]);

  // Build the county map SVG (counties shaded by assigned distributor).
  const svgInner = useMemo(() => {
    if (!geo) return "";
    const regions = Object.entries(assignments).map(([code, dist]) => ({
      level: "county", region_code: code, distributor: dist, active: true,
    }));
    const distByName = new Map(names.map((n) => [n, { color_hex: colorOf.get(n) }]));
    const fills = resolveFills(regions, distByName, "county");
    return buildMapSvg({
      fills, geometry: geo, useCallouts: false, showLabels: false,
      unassignedColor: "#e5e7eb", labelColor: "#1f2937",
    });
  }, [geo, assignments, names, colorOf]);

  const viewBox = useMemo(() => zoomViewBox(bbox || []), [bbox]);
  const countyOn = mode === "county";

  function codeFrom(e) {
    const el = e.target.closest?.("[data-code]");
    return el ? el.getAttribute("data-code") : null;
  }

  // Optimistic write: update local state, persist, revert+report on failure.
  async function assignCounty(countyFips, distributor) {
    const prev = assignments[countyFips] || null;
    const next = distributor || null;
    if (prev === next) return;
    setAssignments((a) => {
      const copy = { ...a };
      if (next) copy[countyFips] = next; else delete copy[countyFips];
      return copy;
    });
    dirty.current = true;
    try {
      await setCountyDistributor(countyFips, next || "");
    } catch (e) {
      setErr(e.message);
      setAssignments((a) => {
        const copy = { ...a };
        if (prev) copy[countyFips] = prev; else delete copy[countyFips];
        return copy;
      });
    }
  }

  function handleMapClick(e) {
    if (!countyOn) return;
    const code = codeFrom(e);
    if (!code) return;
    // Armed -> paint with that distributor. Nothing armed -> clear the county.
    assignCounty(code, armed || "");
  }

  async function toggleMode(on) {
    setBusy(true);
    setErr(null);
    try {
      await setSalesStateMode(iso, on ? "county" : "state");
      setMode(on ? "county" : "state");
      dirty.current = true;
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function applyBulk(distributor) {
    if (!geo) return;
    setBusy(true);
    setErr(null);
    const all = geo.codes;
    const next = distributor || "";
    // Optimistic local update.
    const before = assignments;
    setAssignments(() => {
      if (!next) return {};
      const copy = {};
      for (const c of all) copy[c] = next;
      return copy;
    });
    dirty.current = true;
    try {
      await setCountyDistributorBulk(all, next);
    } catch (e) {
      setErr(e.message);
      setAssignments(before);
    } finally {
      setBusy(false);
    }
  }

  function addDistributor() {
    const n = newDist.trim();
    if (!n) return;
    setExtraNames((x) => [...x, n]);
    setArmed(n);
    setNewDist("");
  }

  function close() {
    if (dirty.current) onChanged?.();
    onClose?.();
  }

  const visibleCounties = useMemo(() => {
    if (!geo) return [];
    const q = search.trim().toLowerCase();
    return geo.codes
      .map((c) => ({ fips: c, name: geo.names?.[c] || c }))
      .filter((c) => !q || c.name.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [geo, search]);

  const assignedCount = Object.keys(assignments).length;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={`Counties — ${stateName}`} onClick={close}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 900, maxWidth: "94vw", maxHeight: "92vh", overflowY: "auto", padding: 22 }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 className="modal-title" style={{ marginBottom: 0 }}>{stateName} — distribution by county</h2>
          <button className="link" aria-label="Close" onClick={close} style={{ fontSize: 22 }}>×</button>
        </div>

        {/* Master toggle */}
        <label className="checkbox" style={{ marginTop: 14, display: "inline-flex", gap: 8, alignItems: "center" }}>
          <input type="checkbox" checked={countyOn} disabled={busy || loading || !iso} onChange={(e) => toggleMode(e.target.checked)} />
          <span>Distribute {stateName} by county</span>
        </label>
        <p className={countyOn && !assignedCount ? "warning" : "muted"} style={{ fontSize: 12, marginTop: 6 }}>
          {!countyOn
            ? "Off — this state uses its whole-state distributor (from the States table). Turn on to split it by county."
            : assignedCount
              ? "Assign a distributor to each county. On distributor maps this state shows a hatch nationally and drills in to these counties."
              // County mode only takes effect once a county is assigned — map-public
              // falls back to the whole-state distributor until then. Say so, or the
              // toggle looks applied while the published map is unchanged.
              : `No counties assigned yet, so maps still show ${stateName} in its whole-state distributor's color. Assign the first county below and it switches to the county view.`}
        </p>

        {err && <p className="error" style={{ marginTop: 8 }}>{err}</p>}
        {loading ? (
          <p className="muted" style={{ marginTop: 16 }}>Loading counties…</p>
        ) : !countyOn ? null : (
          <div style={{ display: "flex", gap: 18, marginTop: 14, flexWrap: "wrap" }}>
            {/* Left: paintable county map */}
            <div style={{ flex: "1 1 420px", minWidth: 320 }}>
              <svg
                viewBox={viewBox}
                preserveAspectRatio="xMidYMid meet"
                role="img"
                aria-label={`${stateName} counties`}
                style={{ width: "100%", height: "auto", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, cursor: armed ? "crosshair" : "pointer" }}
                onClick={handleMapClick}
                onMouseOver={(e) => setHoverFips(codeFrom(e))}
                onMouseLeave={() => setHoverFips(null)}
                dangerouslySetInnerHTML={{ __html: svgInner }}
              />
              <p className="muted" style={{ fontSize: 12, marginTop: 6, minHeight: 18 }}>
                {hoverFips
                  ? `${geo.names?.[hoverFips] || hoverFips}${assignments[hoverFips] ? ` — ${assignments[hoverFips]}` : ""}`
                  : armed
                    ? `Painting: ${armed} — click counties to assign. Click an assigned county with nothing selected to clear.`
                    : "Select a distributor on the right, then click counties to paint them."}
              </p>
              <p className="muted" style={{ fontSize: 12 }}>{assignedCount} of {geo.codes.length} counties assigned</p>
            </div>

            {/* Right: distributor palette + county list */}
            <div style={{ flex: "1 1 320px", minWidth: 280 }}>
              <h3 style={{ margin: "0 0 8px", fontSize: 14 }}>Distributors</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <button
                  type="button"
                  className={armed === null ? "seg on" : "seg"}
                  onClick={() => setArmed(null)}
                  style={{ textAlign: "left", padding: "6px 10px", borderRadius: 8 }}
                  title="Eraser — click counties to clear them"
                >
                  ⌫ Clear (eraser)
                </button>
                {names.length === 0 && <p className="muted" style={{ fontSize: 12 }}>No distributors yet — add one below.</p>}
                {names.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setArmed(armed === n ? null : n)}
                    aria-pressed={armed === n}
                    style={{
                      display: "flex", alignItems: "center", gap: 8, textAlign: "left",
                      padding: "6px 10px", borderRadius: 8, cursor: "pointer",
                      background: armed === n ? "var(--primary-soft)" : "transparent",
                      border: armed === n ? "2px solid var(--primary)" : "1px solid var(--border)",
                    }}
                  >
                    <span style={{ width: 16, height: 16, borderRadius: 4, background: colorOf.get(n), flex: "0 0 auto" }} />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n}</span>
                  </button>
                ))}
              </div>

              {names.length > 0 && (
                <p className="muted" style={{ fontSize: 11, margin: "8px 0 0" }}>
                  These colors are for telling distributors apart while you assign
                  counties. Each map keeps its own colors, set in the map editor.
                </p>
              )}

              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                <input
                  value={newDist}
                  onChange={(e) => setNewDist(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDistributor(); } }}
                  placeholder="Add distributor…"
                  maxLength={160}
                  style={{ flex: "1 1 auto", margin: 0 }}
                />
                <button type="button" className="secondary" onClick={addDistributor} disabled={!newDist.trim()}>Add</button>
              </div>

              <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 12 }}>
                <span className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>Set all to</span>
                <select value={bulkName} onChange={(e) => setBulkName(e.target.value)} style={{ flex: "1 1 auto" }}>
                  <option value="">— Unassigned —</option>
                  {names.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <button type="button" className="secondary" disabled={busy} onClick={() => applyBulk(bulkName)}>Apply</button>
              </div>

              <h3 style={{ margin: "16px 0 8px", fontSize: 14 }}>Counties</h3>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${geo.codes.length} counties…`}
                style={{ width: "100%", margin: "0 0 8px" }}
              />
              <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 8 }}>
                {visibleCounties.map((c) => (
                  <div key={c.fips} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", borderBottom: "1px solid var(--border)" }}>
                    <span style={{ width: 12, height: 12, borderRadius: 3, flex: "0 0 auto", background: assignments[c.fips] ? colorOf.get(assignments[c.fips]) : "transparent", border: assignments[c.fips] ? "none" : "1px solid var(--border)" }} />
                    <span style={{ flex: "1 1 auto", fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
                    <select
                      value={assignments[c.fips] || ""}
                      onChange={(e) => assignCounty(c.fips, e.target.value)}
                      style={{ flex: "0 0 150px", fontSize: 12 }}
                    >
                      <option value="">— Unassigned —</option>
                      {names.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn" type="button" onClick={close}>Done</button>
        </div>
      </div>
    </div>
  );
}
